import { useList } from "@refinedev/core";
import { Col, Row } from "antd";
import {
  VideoSummaryStats,
  VideoPositionPoint,
  VideoCompletionBreakdown,
  DocumentCompletionBreakdown,
} from "types/";
import { useAnalyticsPage } from "./hooks/useAnalyticsPage";
import { useMediaTimings } from "./hooks/useMediaTimings";
import { AnalyticsSubPageLayout } from "../components/analytics/AnalyticsSubPageLayout";
import { CompactStatCard } from "../components/analytics/CompactStatCard";
import { AllVersionsTag } from "../components/analytics/AllVersionsTag";
import {
  buildDateFilters,
  buildVersionFilters,
  showsOneVersion,
} from "../components/analytics/utils/";
import { ViewerRetentionChart } from "../components/analytics/ViewerRetentionChart";
import { SeekPatternsChart } from "../components/analytics/SeekPatternsChart";
import { ViewerSegmentsChart } from "../components/analytics/ViewerSegmentsChart";
import {
  ANALYTICS_STALE_TIME,
  TARGETS,
} from "../components/analytics/constants/constants";
import { ComparisonCard } from "../components/analytics/ComparisonCard";
import { PlayFunnelCard } from "../components/analytics/PlayFunnelCard";
import { SpeedDistributionChart } from "../components/analytics/SpeedDistributionChart";

const T_PREFIX = "media.projects.analytics.videoExplainer";

export function VideoExplainerAnalyticsPage() {
  const page = useAnalyticsPage<VideoSummaryStats>({
    analyticsType: "video",
    versioned: true,
    summaryFollowsVersion: true,
  });
  const {
    summaryData: videoData,
    isDataLoading,
    t,
    projectId,
    dateRange,
    project,
  } = page;

  const mediaId = project?.media?.[0]?.id;
  const { chapters, durationSeconds } = useMediaTimings(mediaId);

  // Video length used as the charts' x-axis end: from chapter timings, or the
  // project's configured duration when timings aren't available.
  const videoDurationSeconds = durationSeconds ?? project?.settings.duration;

  const dateFilters = buildDateFilters(dateRange);
  const versionFilters = buildVersionFilters(page.version?.requested);
  // These endpoints take no version: say so while one version is shown.
  const oneVersion = showsOneVersion(page.version);
  const allVersionsTag = <AllVersionsTag hidden={!oneVersion} />;
  const documentTag = <AllVersionsTag part="document" hidden={!oneVersion} />;
  const completionEnabled =
    !!projectId && !!dateRange.startDate && !!dateRange.endDate;

  const {
    data: positionAnalysis,
    isLoading: isPositionLoading,
    isFetching: isPositionFetching,
  } = useList<VideoPositionPoint>({
    resource: `analytics/projects/${projectId}/analytics/video/position-analysis`,
    config: { filters: [...dateFilters, ...versionFilters] },
    queryOptions: {
      enabled: !!projectId && completionEnabled,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const {
    data: videoCompletion,
    isLoading: isVideoCompletionLoading,
    isFetching: isVideoCompletionFetching,
  } = useList<VideoCompletionBreakdown>({
    resource: `analytics/projects/${projectId}/analytics/video/completion`,
    config: { filters: [...dateFilters, ...versionFilters] },
    queryOptions: {
      enabled: completionEnabled,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const {
    data: docCompletion,
    isLoading: isDocCompletionLoading,
    isFetching: isDocCompletionFetching,
  } = useList<DocumentCompletionBreakdown>({
    resource: `analytics/projects/${projectId}/analytics/document/completion`,
    config: { filters: dateFilters },
    queryOptions: {
      enabled: completionEnabled,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const positionData = positionAnalysis?.data;
  const isPositionDataLoading = isPositionLoading || isPositionFetching;
  const videoCompletionData =
    videoCompletion?.data as unknown as VideoCompletionBreakdown;
  const docCompletionData =
    docCompletion?.data as unknown as DocumentCompletionBreakdown;
  const isCompletionLoading =
    isVideoCompletionLoading ||
    isVideoCompletionFetching ||
    isDocCompletionLoading ||
    isDocCompletionFetching;

  return (
    <AnalyticsSubPageLayout
      authKey="videoExplainerAnalytics"
      translationPrefix={T_PREFIX}
      page={page}
    >
      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.completion`)}
            metric={videoData?.avg_completion_pct}
            target={TARGETS.videoExplainer.avg_completion_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.completionTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.totalSessions`)}
            metric={videoData?.impression_sessions}
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.totalSessionsTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.medianDropOff`)}
            metric={videoData?.median_drop_seconds}
            target={TARGETS.videoExplainer.median_drop_seconds}
            aim="high"
            type="seconds"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.medianDropOffTooltip`)}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.fullCompleters`)}
            metric={videoData?.full_completers_pct}
            target={TARGETS.videoExplainer.full_completers_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.fullCompletersTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.playRate`)}
            metric={videoData?.play_rate_pct}
            target={TARGETS.videoExplainer.play_rate}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.playRateTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.chatRate`)}
            metric={videoData?.chat_rate_pct}
            target={TARGETS.videoExplainer.chat_rate_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.chatRateTooltip`)}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={12}>
          <ViewerRetentionChart
            data={positionData}
            chapters={chapters}
            maxSeconds={videoDurationSeconds}
            loading={isPositionDataLoading}
          />
        </Col>
        <Col sm={24} md={12}>
          <SeekPatternsChart
            data={positionData}
            chapters={chapters}
            maxSeconds={videoDurationSeconds}
            loading={isPositionDataLoading}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <ComparisonCard
            badge={documentTag}
            title={t(
              "media.projects.analytics.dashboard.summaryCards.completionByTime.title"
            )}
            videoCompletionData={videoCompletionData}
            docCompletionData={docCompletionData}
            breakdownKey="by_time"
            loading={isCompletionLoading}
          />
        </Col>
        <Col sm={24} md={8}>
          <ComparisonCard
            badge={documentTag}
            title={t(
              "media.projects.analytics.dashboard.summaryCards.completionByDevice.title"
            )}
            videoCompletionData={videoCompletionData}
            docCompletionData={docCompletionData}
            breakdownKey="by_device"
            loading={isCompletionLoading}
          />
        </Col>
        <Col sm={24} md={8}>
          <ComparisonCard
            badge={documentTag}
            title={t(
              "media.projects.analytics.dashboard.summaryCards.completionBySource.title"
            )}
            videoCompletionData={videoCompletionData}
            docCompletionData={docCompletionData}
            breakdownKey="by_source"
            loading={isCompletionLoading}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <ViewerSegmentsChart
            projectId={projectId}
            dateRange={dateRange}
            badge={allVersionsTag}
          />
        </Col>
        <Col sm={24} md={8}>
          <PlayFunnelCard
            data={{
              impressionSessions: videoData?.impression_sessions ?? 0,
              playSessions: videoData?.play_sessions ?? 0,
              playRate: videoData?.play_rate_pct ?? 0,
            }}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={8}>
          <SpeedDistributionChart
            projectId={projectId}
            badge={allVersionsTag}
          />
        </Col>
      </Row>
    </AnalyticsSubPageLayout>
  );
}
